import React from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import { TRIP_JOIN_MIN_DRAW_M, type TripLandmark, type TripLineString, type TripPeak } from "@atlas/shared";
import { MAP_COLORS } from "./map-colors";
import { createMarkerImage, LANDMARK_GLYPHS, PEAK_GLYPH, LANDMARK_MARKER_PX, PEAK_MARKER_PX } from "./AtlasMap";

// Trip route map (spec 35): every member track in order, one colour per day,
// thin dashed grey connectors for the gaps between recordings (display only,
// never counted), stretches filled from a planned route (spec 38: day colour,
// thinner and dotted), a tent marker at each overnight join, and the collected
// peak/landmark markers the explore map uses. Shared by the owner's trip page
// and the public trip share page.

const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";

/** Day colours, cycled. Existing map tokens only (no new hues). */
// Same order as the share images (TRIP_DAY_COLORS in packages/api/src/lib/trip-share.ts):
// gold, sky, spruce, then the platinum and silver tier hexes (--tier-*).
export const TRIP_DAY_COLORS = [MAP_COLORS.gold, MAP_COLORS.sky, MAP_COLORS.spruce, "#8FE0E6", "#B4C1CD"] as const;

export function tripDayColor(dayIndex: number): string {
  return TRIP_DAY_COLORS[((dayIndex % TRIP_DAY_COLORS.length) + TRIP_DAY_COLORS.length) % TRIP_DAY_COLORS.length]!;
}

export interface TripMapTrack {
  dayIndex: number;
  geojson: TripLineString | null;
}

export interface TripMapJoin {
  distanceM: number;
  overnight: boolean;
  /** Spec 38: covered by a route fill: no dashed connector (a camp still shows). */
  filled?: boolean;
  geometry: TripLineString;
  /** Round 2: where the night was spent. Null/absent on older payloads: the join's start. */
  camp?: { latitude: number; longitude: number; name: string | null } | null;
}

/** Where an overnight join's camp marker sits. */
export function campPosition(j: TripMapJoin): [number, number] | null {
  if (j.camp) return [j.camp.longitude, j.camp.latitude];
  const c = j.geometry.coordinates[0];
  return c ? [c[0], c[1]] : null;
}

/** Spec 38: a gap filled from the planned route. */
export interface TripMapFill {
  dayIndex: number;
  geometry: TripLineString;
}

export interface TripMapPinClick {
  kind: "peak" | "landmark";
  id: string;
  slug: string;
  name: string;
}

export interface TripRouteMapProps {
  tracks: TripMapTrack[];
  joins: TripMapJoin[];
  /** Spec 38: pieces filled from the planned route. */
  fills?: TripMapFill[];
  peaks: TripPeak[];
  landmarks: TripLandmark[];
  padding?: number | { top: number; right: number; bottom: number; left: number };
  interactive?: boolean;
  onPinClick?: (pin: TripMapPinClick) => void;
  /** Round 2 (move a camp): while true, a map click calls onMapPick and the cursor is a crosshair. */
  pickMode?: boolean;
  onMapPick?: (lngLat: { lat: number; lng: number }) => void;
  /** A pending camp position, drawn as a pin until saved. */
  pendingPin?: { lat: number; lng: number } | null;
  style?: React.CSSProperties;
}

type FC = { type: "FeatureCollection"; features: unknown[] };

const NO_FILLS: TripMapFill[] = [];

// lucide "tent" (24px viewBox), drawn into the camp marker.
const TENT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 21 14 3"/><path d="M20.5 21 10 3"/><path d="M15.5 21 12 15l-3.5 6"/><path d="M2 21h20"/></svg>';

function bboxOf(props: Pick<TripRouteMapProps, "tracks" | "fills" | "peaks" | "landmarks">): [number, number, number, number] | null {
  let w = Infinity,
    s = Infinity,
    e = -Infinity,
    n = -Infinity;
  const push = (lng: number, lat: number) => {
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  };
  for (const t of props.tracks) for (const c of t.geojson?.coordinates ?? []) push(c[0], c[1]);
  for (const f of props.fills ?? []) for (const c of f.geometry.coordinates) push(c[0], c[1]);
  for (const p of props.peaks) push(p.longitude, p.latitude);
  for (const l of props.landmarks) push(l.longitude, l.latitude);
  return isFinite(w) ? [w, s, e, n] : null;
}

function buildSources(props: Pick<TripRouteMapProps, "tracks" | "joins" | "fills" | "peaks" | "landmarks">): {
  tracks: FC;
  joins: FC;
  fills: FC;
  pins: FC;
  ends: FC;
} {
  const tracks: FC = {
    type: "FeatureCollection",
    features: props.tracks
      .filter((t) => t.geojson && t.geojson.coordinates.length >= 2)
      .map((t) => ({ type: "Feature", geometry: t.geojson, properties: { color: tripDayColor(t.dayIndex) } })),
  };
  const joins: FC = {
    type: "FeatureCollection",
    features: props.joins
      .filter((j) => !j.filled && j.distanceM >= TRIP_JOIN_MIN_DRAW_M && j.geometry.coordinates.length >= 2)
      .map((j) => ({ type: "Feature", geometry: j.geometry, properties: {} })),
  };
  const fills: FC = {
    type: "FeatureCollection",
    features: (props.fills ?? [])
      .filter((f) => f.geometry.coordinates.length >= 2)
      .map((f) => ({ type: "Feature", geometry: f.geometry, properties: { color: tripDayColor(f.dayIndex) } })),
  };
  const badgedPeaks = new Set(
    props.landmarks.filter((l) => l.category === "trig_point" && l.peakId).map((l) => l.peakId as string),
  );
  const peakIds = new Set(props.peaks.map((p) => p.id));
  const pins: FC = {
    type: "FeatureCollection",
    features: [
      ...props.peaks.map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.longitude, p.latitude] },
        properties: {
          kind: "peak",
          id: p.id,
          slug: p.slug,
          name: p.name,
          icon: badgedPeaks.has(p.id) ? "peak-collected-trig" : "peak-collected",
        },
      })),
      ...props.landmarks
        .filter((l) => !(l.category === "trig_point" && l.peakId && peakIds.has(l.peakId)))
        .map((l) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [l.longitude, l.latitude] },
          properties: {
            kind: "landmark",
            id: l.id,
            slug: l.slug,
            name: l.name,
            icon: `lm-${l.category in LANDMARK_GLYPHS ? l.category : "trig_point"}-c`,
          },
        })),
    ],
  };
  const drawn = props.tracks.filter((t) => t.geojson && t.geojson.coordinates.length >= 2);
  const first = drawn[0]?.geojson?.coordinates[0];
  const lastCoords = drawn[drawn.length - 1]?.geojson?.coordinates;
  const last = lastCoords?.[lastCoords.length - 1];
  const ends: FC = {
    type: "FeatureCollection",
    features: [
      ...(first ? [{ type: "Feature", geometry: { type: "Point", coordinates: first }, properties: { end: "start" } }] : []),
      ...(last ? [{ type: "Feature", geometry: { type: "Point", coordinates: last }, properties: { end: "finish" } }] : []),
    ],
  };
  return { tracks, joins, fills, pins, ends };
}

export function TripRouteMap({
  tracks,
  joins,
  fills = NO_FILLS,
  peaks,
  landmarks,
  padding = 48,
  interactive = true,
  onPinClick,
  pickMode = false,
  onMapPick,
  pendingPin = null,
  style,
}: TripRouteMapProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const campMarkersRef = React.useRef<maplibregl.Marker[]>([]);
  const propsRef = React.useRef({ tracks, joins, fills, peaks, landmarks });
  propsRef.current = { tracks, joins, fills, peaks, landmarks };
  const paddingRef = React.useRef(padding);
  paddingRef.current = padding;
  const onPinClickRef = React.useRef(onPinClick);
  onPinClickRef.current = onPinClick;
  const pickModeRef = React.useRef(pickMode);
  pickModeRef.current = pickMode;
  const onMapPickRef = React.useRef(onMapPick);
  onMapPickRef.current = onMapPick;
  const pendingMarkerRef = React.useRef<maplibregl.Marker | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-1.5, 53],
      zoom: 7,
      attributionControl: false,
      interactive,
      fadeDuration: 0,
    });
    mapRef.current = map;
    if (interactive) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      for (const layer of map.getStyle().layers ?? []) {
        if ("source-layer" in layer && layer["source-layer"] === "aeroway") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }
      const sources = buildSources(propsRef.current);
      map.addSource("trip-joins", { type: "geojson", data: sources.joins as never });
      map.addSource("trip-fills", { type: "geojson", data: sources.fills as never });
      map.addSource("trip-tracks", { type: "geojson", data: sources.tracks as never });
      map.addSource("trip-pins", { type: "geojson", data: sources.pins as never });
      map.addSource("trip-ends", { type: "geojson", data: sources.ends as never });

      map.addLayer({
        id: "trip-joins-line",
        type: "line",
        source: "trip-joins",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": MAP_COLORS.grey, "line-width": 2, "line-dasharray": [2, 2], "line-opacity": 0.9 },
      });
      // Filled from the planned route: the day's colour, thinner and dotted, so
      // it never reads as recorded track (spec 38).
      map.addLayer({
        id: "trip-fills-halo",
        type: "line",
        source: "trip-fills",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "rgba(9,12,16,0.5)", "line-width": 6 },
      });
      map.addLayer({
        id: "trip-fills-line",
        type: "line",
        source: "trip-fills",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-dasharray": [0.1, 2], "line-opacity": 0.95 },
      });
      map.addLayer({
        id: "trip-tracks-halo",
        type: "line",
        source: "trip-tracks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "rgba(9,12,16,0.55)", "line-width": 10 },
      });
      map.addLayer({
        id: "trip-tracks-line",
        type: "line",
        source: "trip-tracks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 4, "line-opacity": 0.95 },
      });
      map.addImage("peak-collected", createMarkerImage(MAP_COLORS.gold, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
      map.addImage(
        "peak-collected-trig",
        createMarkerImage(MAP_COLORS.gold, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true }),
        { pixelRatio: 2 },
      );
      for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
        map.addImage(`lm-${category}-c`, createMarkerImage(MAP_COLORS.gold, LANDMARK_MARKER_PX, glyph), {
          pixelRatio: 2,
        });
      }
      map.addLayer({
        id: "trip-pins-symbol",
        type: "symbol",
        source: "trip-pins",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-allow-overlap": true,
          "icon-anchor": "center",
          "symbol-sort-key": ["match", ["get", "kind"], "peak", 0, 1],
        },
      });
      map.addLayer({
        id: "trip-ends-circle",
        type: "circle",
        source: "trip-ends",
        paint: {
          "circle-color": ["match", ["get", "end"], "finish", MAP_COLORS.textOnGrey, MAP_COLORS.textOnGold],
          "circle-radius": 5.5,
          "circle-stroke-width": 2,
          "circle-stroke-color": ["match", ["get", "end"], "finish", MAP_COLORS.textOnGold, MAP_COLORS.textOnGrey],
        },
      });

      if (interactive) {
        // Camp picking wins over pin clicks while it is on.
        map.on("click", (e) => {
          if (!pickModeRef.current) return;
          onMapPickRef.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        });
        map.on("click", "trip-pins-symbol", (e) => {
          if (pickModeRef.current) return;
          const p = e.features?.[0]?.properties as { kind?: string; id?: string; slug?: string; name?: string } | undefined;
          if (!p?.id || !p.slug || (p.kind !== "peak" && p.kind !== "landmark")) return;
          onPinClickRef.current?.({ kind: p.kind, id: p.id, slug: p.slug, name: p.name ?? "" });
        });
        map.on("mouseenter", "trip-pins-symbol", () => {
          if (!pickModeRef.current) map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "trip-pins-symbol", () => {
          if (!pickModeRef.current) map.getCanvas().style.cursor = "";
        });
      }

      const bbox = bboxOf(propsRef.current);
      if (bbox) {
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: paddingRef.current, duration: 0, maxZoom: 15 });
      }
      setLoaded(true);
    });

    return () => {
      for (const m of campMarkersRef.current) m.remove();
      campMarkersRef.current = [];
      pendingMarkerRef.current?.remove();
      pendingMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data changes after load: refresh sources and camp markers.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const sources = buildSources({ tracks, joins, fills, peaks, landmarks });
    (map.getSource("trip-joins") as maplibregl.GeoJSONSource | undefined)?.setData(sources.joins as never);
    (map.getSource("trip-fills") as maplibregl.GeoJSONSource | undefined)?.setData(sources.fills as never);
    (map.getSource("trip-tracks") as maplibregl.GeoJSONSource | undefined)?.setData(sources.tracks as never);
    (map.getSource("trip-pins") as maplibregl.GeoJSONSource | undefined)?.setData(sources.pins as never);
    (map.getSource("trip-ends") as maplibregl.GeoJSONSource | undefined)?.setData(sources.ends as never);

    for (const m of campMarkersRef.current) m.remove();
    campMarkersRef.current = joins
      .filter((j) => j.overnight && campPosition(j))
      .map((j, i) => {
        const el = document.createElement("div");
        const name = j.camp?.name ?? null;
        el.title = name ? `Night ${i + 1}: ${name}` : `Night ${i + 1}`;
        el.setAttribute("aria-label", name ? `Camp, night ${i + 1}, ${name}` : `Camp, night ${i + 1}`);
        el.style.cssText = [
          "width:26px",
          "height:26px",
          "border-radius:999px",
          "display:grid",
          "place-items:center",
          `background:${MAP_COLORS.textOnGold}`,
          `color:${MAP_COLORS.textOnGrey}`,
          `border:2px solid ${MAP_COLORS.textOnGrey}`,
          "box-shadow:0 1px 4px rgba(0,0,0,.5)",
        ].join(";");
        el.innerHTML = TENT_SVG;
        return new maplibregl.Marker({ element: el }).setLngLat(campPosition(j)!).addTo(map);
      });
  }, [loaded, tracks, joins, fills, peaks, landmarks]);

  // Pick mode: crosshair cursor; the pending camp pin.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    map.getCanvas().style.cursor = pickMode ? "crosshair" : "";
  }, [loaded, pickMode]);

  React.useEffect(() => {
    const map = mapRef.current;
    pendingMarkerRef.current?.remove();
    pendingMarkerRef.current = null;
    if (!map || !loaded || !pendingPin) return;
    pendingMarkerRef.current = new maplibregl.Marker({ color: MAP_COLORS.gold })
      .setLngLat([pendingPin.lng, pendingPin.lat])
      .addTo(map);
  }, [loaded, pendingPin]);

  return <div ref={containerRef} style={{ position: "absolute", inset: 0, ...style }} />;
}

/** Map legend: one swatch per day, the dashed gap and the camp marker. */
export function TripMapLegend({
  days,
  hasJoins,
  hasCamps,
  hasFills = false,
  corner = "bottom-left",
  className,
}: {
  days: number;
  hasJoins: boolean;
  hasCamps: boolean;
  /** Spec 38: show the "From your route" swatch. */
  hasFills?: boolean;
  corner?: "bottom-left" | "bottom-right";
  className?: string;
}) {
  const shown = Math.min(days, TRIP_DAY_COLORS.length);
  return (
    <div
      className={["atlas-glass", className].filter(Boolean).join(" ")}
      style={{
        position: "absolute",
        ...(corner === "bottom-right" ? { right: 12 } : { left: 12 }),
        bottom: 12,
        zIndex: 5,
        padding: "8px 12px",
        borderRadius: "var(--radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 5,
        fontSize: 12,
        color: "var(--text-secondary)",
        maxWidth: "calc(100% - 24px)",
      }}
    >
      {Array.from({ length: shown }, (_, i) => (
        <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 18, height: 4, borderRadius: 2, background: tripDayColor(i) }} />
          {days > TRIP_DAY_COLORS.length && i === shown - 1 ? `Day ${i + 1} (colours repeat)` : `Day ${i + 1}`}
        </span>
      ))}
      {hasFills && (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span aria-hidden style={{ width: 18, height: 0, borderTop: `3px dotted ${tripDayColor(0)}` }} />
          From your route
        </span>
      )}
      {hasJoins && (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 18,
              height: 0,
              borderTop: `2px dashed ${MAP_COLORS.grey}`,
            }}
          />
          Not recorded
        </span>
      )}
      {hasCamps && (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            aria-hidden
            style={{
              width: 18,
              height: 18,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: MAP_COLORS.textOnGold,
              color: MAP_COLORS.textOnGrey,
              border: `1.5px solid ${MAP_COLORS.textOnGrey}`,
            }}
            dangerouslySetInnerHTML={{ __html: TENT_SVG.replace(/width="14" height="14"/, 'width="10" height="10"') }}
          />
          Camp
        </span>
      )}
    </div>
  );
}
